import { toast } from 'sonner';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import api from '../api';

function AdminWaitlist() {
  const [allEntries, setAllEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [filter, setFilter] = useState('pending');
  const { t } = useTranslation();

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      // Fetch ALL entries (no filter) to calculate counters correctly
      const response = await api.get('/api/admin/waitlist');
      setAllEntries(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter entries based on selected filter
  const filteredEntries = filter 
    ? allEntries.filter(e => e.status === filter)
    : allEntries;

  const handleApprove = async (entryId, entryData) => {
    if (!confirm(`¿Aprobar a ${entryData.nombre}?\n\nSe creará una cuenta de prestamista y se enviará email con credenciales.`)) {
      return;
    }

    setProcessing(entryId);
    try {
      const response = await api.post(`/api/admin/waitlist/${entryId}/approve`);
      toast.success(`Prestamista creado correctamente\n\nEmail: ${entryData.email}\nContraseña temporal: ${response.data.temp_password}\n\n(Se envió email automático)`);
      fetchEntries();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || 'Error al aprobar'));
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (entryId, nombre) => {
    const reason = prompt(`¿Rechazar a ${nombre}?\n\nRazón (opcional):`);
    if (reason === null) return;

    setProcessing(entryId);
    try {
      await api.post(`/api/admin/waitlist/${entryId}/reject`, { rejection_reason: reason });
      toast.success('Entrada rechazada i email enviat');
      fetchEntries();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || 'Error'));
    } finally {
      setProcessing(null);
    }
  };

  const handleRestore = async (entryId, nombre) => {
    const confirmed = window.confirm(`Restaurar a ${nombre} a la llista de pendents?`);
    if (!confirmed) return;

    setProcessing(entryId);
    try {
      await api.post(`/api/admin/waitlist/${entryId}/restore`);
      toast.success('Entrada restaurada');
      fetchEntries();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || 'Error'));
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div className="main-layout">
        <Sidebar />
        <div className="content-area" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-layout">
      <Sidebar />
      <div className="content-area">
        <TopBar title={t('sidebar.waitlist')} />

        <main style={{ padding: '2.5rem' }} className="fade-in">
          {/* Filters - Compact on mobile */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
            {[
              { value: 'pending', label: t('waitlistAdmin.pending'), count: allEntries.filter(e => e.status === 'pending').length, color: '#FF9800' },
              { value: 'approved', label: t('waitlistAdmin.approved'), count: allEntries.filter(e => e.status === 'approved').length, color: '#007a33' },
              { value: 'rejected', label: t('waitlistAdmin.rejected'), count: allEntries.filter(e => e.status === 'rejected').length, color: '#616161' },
              { value: '', label: t('status.all'), count: allEntries.length, color: '#103766' }
            ].map(({value, label, count, color}) => (
              <button
                key={value}
                onClick={() => setFilter(value)}
                className="waitlist-tab-button"
                style={{
                  padding: '0.75rem 1.5rem',
                  background: filter === value ? color : 'rgba(255,255,255,0.05)',
                  color: filter === value ? 'white' : 'rgba(255,255,255,0.7)',
                  border: filter === value ? 'none' : '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  flex: '1 1 auto',
                  minWidth: 'fit-content',
                  whiteSpace: 'nowrap'
                }}
                data-testid={`filter-${value || 'all'}`}
              >
                {label} ({count})
              </button>
            ))}
          </div>

          {/* Entries List */}
          {filteredEntries.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '4rem',
              background: 'rgba(255,255,255,0.03)',
              borderRadius: '20px'
            }}>
              <p style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</p>
              <h3 style={{ fontSize: '1.25rem', color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
                No hi ha entrades en aquesta categoria
              </h3>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '1.5rem' }}>
              {filteredEntries.map((entry) => (
                <div 
                  key={entry._id}
                  className="dark-card"
                  style={{
                    padding: '2rem',
                    border: entry.status === 'pending' ? '2px solid rgba(255, 152, 0, 0.4)' : '1px solid rgba(255,255,255,0.08)'
                  }}
                  data-testid={`waitlist-entry-${entry._id}`}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
                    <div>
                      <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: 'white', marginBottom: '0.5rem' }}>
                        {entry.nombre}
                      </h3>
                      <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.5)' }}>
                        Sol·licitat el {new Date(entry.created_at).toLocaleDateString('ca-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </p>
                    </div>
                    <span style={{
                      padding: '0.5rem 1rem',
                      background: entry.status === 'pending' ? 'rgba(255,152,0,0.2)' : entry.status === 'approved' ? 'rgba(0,122,51,0.2)' : 'rgba(97,97,97,0.2)',
                      color: entry.status === 'pending' ? '#FF9800' : entry.status === 'approved' ? '#007a33' : '#616161',
                      borderRadius: '20px',
                      fontSize: '0.8125rem',
                      fontWeight: '600'
                    }}>
                      {entry.status === 'pending' ? t('waitlistAdmin.pending') : entry.status === 'approved' ? t('waitlistAdmin.approved') : t('waitlistAdmin.rejected')}
                    </span>
                  </div>

                  {/* Info Grid - Mobile friendly */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                    <div>
                      <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Email</p>
                      <p style={{ fontSize: '1rem', fontWeight: '600', color: 'rgba(255,255,255,0.9)' }}>{entry.email}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Telèfon</p>
                      <p style={{ fontSize: '1rem', fontWeight: '600', color: 'rgba(255,255,255,0.9)' }}>{entry.telefono}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Capital Disponible</p>
                      <p style={{ fontSize: '1.25rem', fontWeight: '700', color: '#60a5fa' }}>
                        {entry.capital_disponible.toLocaleString('es-ES')}€
                      </p>
                    </div>
                    <div>
                      <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Tipus Inversió</p>
                      <p style={{ fontSize: '0.95rem', fontWeight: '600', color: 'rgba(255,255,255,0.9)' }}>
                        {entry.tipo_inversion === 'short_term' ? '12 mesos' : entry.tipo_inversion === 'medium_term' ? '24 mesos' : entry.tipo_inversion === 'long_term' ? '36 mesos' : entry.tipo_inversion}
                      </p>
                    </div>
                  </div>

                  {/* Optional Fields */}
                  {entry.invitation_code && (
                    <div style={{
                      padding: '1rem',
                      background: 'rgba(103,119,246,0.1)',
                      borderRadius: '8px',
                      marginBottom: '1.5rem',
                      borderLeft: '3px solid #288cfa'
                    }}>
                      <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
                        <strong>{t('waitlistAdmin.invitationCode')}:</strong> {entry.invitation_code}
                      </p>
                      <p style={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.7)' }}>
                        <strong>{t('waitlistAdmin.recommendedBy')}:</strong> {entry.generated_by_name || t('waitlistAdmin.unknown')}
                      </p>
                    </div>
                  )}

                  {/* Actions */}
                  {entry.status === 'pending' && (
                    <div style={{ display: 'flex', gap: '1rem' }}>
                      <button
                        onClick={() => handleApprove(entry._id, entry)}
                        disabled={processing === entry._id}
                        style={{
                          flex: 1,
                          padding: '1rem',
                          background: 'linear-gradient(135deg, #007a33 0%, #00a843 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '12px',
                          fontWeight: '600',
                          cursor: processing === entry._id ? 'not-allowed' : 'pointer',
                          opacity: processing === entry._id ? 0.5 : 1
                        }}
                        data-testid={`approve-${entry._id}`}
                      >
                        {processing === entry._id ? '⏳ Processant...' : '✓ Aprovar i Crear Compte'}
                      </button>
                      <button
                        onClick={() => handleReject(entry._id, entry.nombre)}
                        disabled={processing === entry._id}
                        style={{
                          padding: '1rem 2rem',
                          background: 'rgba(239,68,68,0.1)',
                          border: '1px solid rgba(239,68,68,0.3)',
                          color: '#ef4444',
                          borderRadius: '12px',
                          fontWeight: '600',
                          cursor: processing === entry._id ? 'not-allowed' : 'pointer'
                        }}
                        data-testid={`reject-${entry._id}`}
                      >
                        ✕ Rebutjar
                      </button>
                    </div>
                  )}
                  
                  {/* Restore button for rejected entries */}
                  {entry.status === 'rejected' && (
                    <div>
                      {entry.rejection_reason && (
                        <div style={{
                          padding: '1rem',
                          background: 'rgba(239,68,68,0.1)',
                          borderRadius: '8px',
                          marginBottom: '1rem'
                        }}>
                          <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.5rem' }}>
                            RAÓ DEL REBUIG:
                          </p>
                          <p style={{ fontSize: '0.875rem', color: '#ef4444' }}>
                            {entry.rejection_reason}
                          </p>
                        </div>
                      )}
                      <button
                        onClick={() => handleRestore(entry._id, entry.nombre)}
                        disabled={processing === entry._id}
                        style={{
                          width: '100%',
                          padding: '1rem',
                          background: 'rgba(255,152,0,0.1)',
                          border: '1px solid rgba(255,152,0,0.3)',
                          color: '#FF9800',
                          borderRadius: '12px',
                          fontWeight: '600',
                          cursor: processing === entry._id ? 'not-allowed' : 'pointer'
                        }}
                        data-testid={`restore-${entry._id}`}
                      >
                        {processing === entry._id ? '⏳ Restaurant...' : '↻ Restaurar a Pendent'}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default AdminWaitlist;
